import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Mic, Clock, ShieldCheck, MapPin, AlertTriangle, Loader2 } from "lucide-react";
import FacebookGroupShareButton from "@/components/FacebookGroupShareButton";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Public voice-note share page. No auth required.
 *
 * Purpose: someone in a tight spot (illegal entry, DV escalation, agent
 * harassment) needs to share a recording with a friend, family member or
 * support worker FAST. They text `/v/:token` and the recipient can listen
 * to the audio + read the transcript + see the timestamp + verify the
 * SHA256 + optionally see where it was recorded.
 *
 * What we DON'T expose: case ID, address, landlord/agent name, the
 * tenant's profile or any other documents. Only what the tenant
 * explicitly opted in to by enabling the share.
 */
export default function PublicVoiceNote() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/public/voice-notes/${token}`);
        if (live) setData(data);
      } catch (e) {
        if (live) setErr(e.response?.data?.detail || "This voice note link is no longer active.");
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => { live = false; };
  }, [token]);

  const fmtDuration = (s) => {
    if (!s && s !== 0) return "";
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${m}:${String(r).padStart(2, "0")}`;
  };

  const fmtDate = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return d.toLocaleString("en-AU", {
        weekday: "short", day: "numeric", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit", second: "2-digit",
        timeZoneName: "short",
      });
    } catch { return iso; }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center p-6">
        <Loader2 size={28} className="animate-spin text-[#1B6C62]" />
      </div>
    );
  }

  if (err) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center p-6" data-testid="public-voice-error">
        <div className="card max-w-md w-full p-8 text-center">
          <AlertTriangle size={32} strokeWidth={1.4} className="mx-auto text-[#B35D4C] mb-3" />
          <h1 className="font-serif text-2xl text-[#0D2B52] mb-2">Link not active</h1>
          <p className="text-sm text-[#6B7280]">{err}</p>
          <p className="text-xs text-[#6B7280] mt-6">
            The tenant may have revoked this link, or the link may have expired.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="public-voice-page">
      <header className="bg-[#0D2B52] text-white py-4 px-6">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <Mic size={20} strokeWidth={1.5} />
          <span className="font-serif text-lg">TenantTrail · Voice note</span>
          <a href="/" className="ml-auto text-xs underline opacity-80 hover:opacity-100">tenanttrail.net</a>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8 space-y-6">

        <div className="card p-6 space-y-5">
          <div>
            <div className="label-mono text-[#1B6C62] mb-1">Shared by a tenant</div>
            <h1 className="font-serif text-2xl text-[#0D2B52]" data-testid="voice-title">
              {data.title}
            </h1>
          </div>

          {/* Audio player */}
          <audio
            controls
            preload="metadata"
            src={`${BACKEND_URL}${data.audio_url}`}
            className="w-full"
            data-testid="voice-public-audio"
          />

          {/* Locked metadata grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div className="bg-[#FAF9F6] border border-[#E5E4DE] p-3">
              <div className="label-mono text-[#1B6C62] mb-1 inline-flex items-center gap-1">
                <Clock size={11} strokeWidth={1.5} /> Recorded
              </div>
              <div className="text-[#333]" data-testid="voice-recorded-at">{fmtDate(data.recorded_at)}</div>
            </div>
            {data.duration_seconds != null && (
              <div className="bg-[#FAF9F6] border border-[#E5E4DE] p-3">
                <div className="label-mono text-[#1B6C62] mb-1">Duration</div>
                <div className="text-[#333] font-mono">{fmtDuration(data.duration_seconds)}</div>
              </div>
            )}
            {data.geo && (
              <div className="bg-[#FAF9F6] border border-[#E5E4DE] p-3 sm:col-span-2">
                <div className="label-mono text-[#1B6C62] mb-1 inline-flex items-center gap-1">
                  <MapPin size={11} strokeWidth={1.5} /> Location at time of recording
                </div>
                <div className="text-[#333] font-mono text-xs">
                  {data.geo.lat.toFixed(5)}, {data.geo.lng.toFixed(5)}
                  {data.geo.accuracy_m ? <span className="text-[#6B7280]"> · ±{Math.round(data.geo.accuracy_m)}m</span> : null}
                </div>
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${data.geo.lat},${data.geo.lng}`}
                  target="_blank" rel="noreferrer"
                  className="text-xs text-[#1B6C62] underline mt-1 inline-block"
                  data-testid="voice-geo-link"
                >
                  Open in Google Maps
                </a>
              </div>
            )}
          </div>

          {data.transcript ? (
            <div className="border-t border-[#EFEDE5] pt-4">
              <div className="label-mono text-[#1B6C62] mb-2 inline-flex items-center gap-1">
                <ShieldCheck size={11} strokeWidth={1.5} /> Transcript (locked)
              </div>
              <p className="text-sm text-[#333] leading-relaxed whitespace-pre-wrap" data-testid="voice-transcript">
                {data.transcript}
              </p>
            </div>
          ) : (
            <div className="border-t border-[#EFEDE5] pt-4 text-xs text-[#6B7280] italic">
              No transcript was generated for this recording.
            </div>
          )}

          {data.sha256 && (
            <div className="border-t border-[#EFEDE5] pt-4">
              <div className="label-mono text-[#1B6C62] mb-1">Audio fingerprint (SHA-256)</div>
              <div className="text-[10px] font-mono text-[#6B7280] break-all" data-testid="voice-sha256">
                {data.sha256}
              </div>
              <p className="text-xs text-[#6B7280] italic mt-2">
                Any edit to the audio file changes this fingerprint. Share this page URL together with the fingerprint to prove the recording hasn&apos;t been tampered with.
              </p>
            </div>
          )}
        </div>

        <div className="card p-5 bg-[#FFF4EC] border-[#F0CDB8]">
          <div className="flex gap-3">
            <AlertTriangle size={18} strokeWidth={1.5} className="text-[#B35D4C] flex-shrink-0 mt-0.5" />
            <div className="text-xs text-[#333] leading-relaxed space-y-2">
              <p>
                This recording was uploaded and timestamped by a tenant using TenantTrail. The transcript and metadata above are server-generated and cannot be edited by the tenant after upload.
              </p>
              <p>
                <span className="font-semibold">TenantTrail does not deliver this link.</span> The tenant chose what to send and to whom. We provide the vault and the tamper-evident page. Nothing more.
              </p>
              <p className="text-[#6B7280]">
                TenantTrail provides this evidence service. We do not vet content and the page does not constitute legal advice. If you are in immediate danger call 000. For confidential domestic-violence support call 1800RESPECT (1800 737 732).
              </p>
            </div>
          </div>
        </div>

        <div className="card p-5">
          <h3 className="font-serif text-lg text-[#0D2B52] mb-1">Stuck in something similar?</h3>
          <p className="text-xs text-[#6B7280] mb-3">
            Renters helping renters. Share this story (or your own) in the TenantTrail Facebook group.
          </p>
          <FacebookGroupShareButton
            url={typeof window !== "undefined" ? window.location.href : ""}
            title={data.title || "Voice note"}
            kind="voice-note"
            slug={data.share_token}
          />
        </div>


        <div className="text-center text-xs text-[#6B7280] py-6">
          Built for Australian renters at <a href="/" className="text-[#1B6C62] underline">tenanttrail.net</a>
        </div>
      </main>
    </div>
  );
}
